import React, { Component, Fragment } from 'react';
import { inject, observer } from 'mobx-react';
import moment from 'moment';
import isArray from 'lodash/isArray';
import { download } from '../../../../../../helpers';
import AirTabbedModal from '../../../../../../common/components/AirTabbedModal';
import { Pagination, Spin, Table, Popover, Select } from 'antd';
import AirButton from '../../../../../../common/components/AirButton';
import AirDatePicker from '../../../../../../common/components/AirDatePicker';
import { ArrowDownIcon, DownloadIcon, GraphIcon } from '../../../../../../common/icons';
import api from '../../../../../../services/api';
import constants from '../../../../../../constants';
import HereService from '../../../../../../services/Here';
import { CartesianGrid, XAxis, YAxis, AreaChart, Area, LineChart, Line, ReferenceLine } from 'recharts';

import './FuelDataModal.css';

const Option = Select.Option;

const ReferenceLabel = ({ ...props }) => {
  const { children, viewBox, axisType = 'xAxis', fontSize, fill, textAnchor = 'middle', offsetX = 0, offsetY = 0 } = props;
  const isVert = axisType === 'yAxis';
  let { x: cx, y: cy, height, width } = viewBox;
  const rot = isVert ? `270 ${cx} ${cy}` : 0;
  if (isVert) {
    cx += width + offsetX;
    cy += offsetY;
  } else {
    cx += offsetX;
    cy += height + offsetY;
  }
  return (
    <text x={cx} y={cy} transform={`rotate(${rot})`} textAnchor={textAnchor} fontSize={fontSize} fill={fill}>
      {children}
    </text>
  );
};

@inject('commonStore')
@observer
class ChartTick extends Component {
  render() {
    const { commonStore } = this.props;
    const { x, y, payload, fontSize = 12, fill = '#000' } = this.props;
    const visible = x < 600 && x > 150;
    return (
      <text x={x} y={y} textAnchor="middle" fontSize={fontSize} fill={fill}>
        {visible ? moment(payload.value).format(commonStore.longDateFormat.L) : ''}
      </text>
    );
  }
}

const API_KEY = constants.HERE_API_KEY;

@inject('translationsStore', 'commonStore', 'noticesStore')
@observer
export default class FuelDataModal extends Component {
  defaultPageSize = 7;
  defaultPagination = {
    total: 0,
    current: 1,
    defaultCurrent: 1,
    hideOnSinglePage: true,
    pageSize: this.defaultPageSize,
    simple: true,
    size: 'small',
    onChange: (page, pageSize) => this.onPageChange(page, pageSize)
  };

  constructor(props) {
    super(props);
    this.mapservice = new HereService(API_KEY);
    this.chartsStyle = {
      fill: 'rgba(73, 198, 190, 0.15)',
      stroke: '#49c6be'
    };
    this.state = {
      loadingFuelData: false,
      loadingFullFuelData: false,
      loadingFuelLevels: false,
      collectionFuelData: [],
      collectionFullFuelData: [],
      collectionFuelLevels: [],
      fuelType: undefined,
      startDate: moment()
        .subtract(7, 'days')
        .startOf('day')
        .toDate(),
      endDate: moment()
        .add(1, 'day')
        .startOf('day')
        .toDate(),
      pagination: false,
      sort: undefined,
      order: undefined
    };
  }

  componentDidMount = () => {
    const { car, visible } = this.props;
    if (car !== undefined && visible) {
      this.setState({ pagination: this.defaultPagination }, () => {
        this.loadFuelData({ car, withFuelData: true, withFullFuelData: true, withFuelLevels: true });
      });
    }
  };

  componentDidUpdate = prevProps => {
    const { car, visible } = this.props;
    if (car !== undefined && visible) {
      if (car.id !== prevProps.car.id || visible !== prevProps.visible) {
        this.setState({ pagination: this.defaultPagination }, () => {
          this.loadFuelData({ car, withFuelData: true, withFullFuelData: true, withFuelLevels: true });
        });
      }
    }
  };

  onFuelDataAvailable = () => {
    const {
      noticesStore,
    } = this.props;
    const { plate } = this.props.car;
    noticesStore.setMultipleDismissedSamePlateType({ plate, type: 'fuel-theft' });
  };

  reverseGeocode = ({ lat, lng, mode = 'retrieveAddresses' }) => {
    return this.mapservice.reverseGeocode(lat, lng, 0, mode);
  };

  solveCollectionsGeocoding = (...rowsCollections) => {
    const toBeDecoded = rowsCollections.map((collection, collectionIndex) => {
      return new Promise(resolve => {
        let pCollection = collection.map((item, index) => {
          if (item.hasOwnProperty('position')) {
            const {
              coordinates: [lng, lat]
            } = item.position;
            return this.reverseGeocode({ lat, lng });
          } else {
            return Promise.resolve();
          }
        });
        Promise.all(pCollection).then(fulfilled => {
          const nuCollection = collection.map((item, index) => {
            if (fulfilled[index] !== undefined) {
              return { ...item, positionLabel: fulfilled[index].hasOwnProperty('Label') ? fulfilled[index].label : '-' };
            } else {
              return item;
            }
          });
          return resolve(nuCollection);
        });
      });
    });
    return Promise.all(toBeDecoded);
  };

  loadFuelData = ({ car, withFuelData = false, withFullFuelData = false, withFuelLevels = false }) => {
    let { pagination, startDate, endDate, fuelType, sort, order } = this.state;
    startDate = moment(startDate).format('YYYY-MM-DD');
    endDate = moment(endDate).format('YYYY-MM-DD');
    let total = 0,
      page = Number(pagination.current),
      pageSize = Number(pagination.pageSize),
      collectionFuelData = [],
      collectionFullFuelData = [],
      collectionFuelLevels = [],
      pFuel = [];
    if (withFuelData) {
      pFuel = [...pFuel, api('getFuelVariations', car.id, startDate, endDate, fuelType, page, pageSize, sort, order)];
    } else {
      pFuel = [...pFuel, Promise.resolve(null)];
    }
    if (withFullFuelData) {
      pFuel = [...pFuel, api('getFuelVariations', car.id, startDate, endDate)];
    } else {
      pFuel = [...pFuel, Promise.resolve(null)];
    }
    if (withFuelLevels) {
      pFuel = [...pFuel, api('getFuelLevels', car.id, startDate, endDate)];
    } else {
      pFuel = [...pFuel, Promise.resolve(null)];
    }
    this.setState({ loadingFuelData: true });
    pFuel = pFuel.map(p => p.catch(error => ({ payload: error, resolved: false })));
    Promise.all(pFuel).then(([fuelData, fullFuelData, levels]) => {
      ({ rows: collectionFuelData } = fuelData || { rows: [] });
      ({ rows: collectionFullFuelData } = fullFuelData || { rows: [] });
      ({ rows: collectionFuelLevels } = levels || { rows: [] });
      this.solveCollectionsGeocoding(collectionFuelData, collectionFullFuelData, collectionFuelLevels).then(
        ([collectionFuelData, collectionFullFuelData, collectionFuelLevels]) => {
          this.setState({ loadingFuelData: false }, () => {
            let toBeSet = {};
            if (fuelData !== null) {
              ({ count: total } = fuelData);
              const pagination = {
                total,
                current: page,
                defaultCurrent: 1,
                hideOnSinglePage: true,
                pageSize: pageSize,
                simple: true,
                size: 'small',
                onChange: (page, pageSize) => this.onPageChange(page, pageSize)
              };
              toBeSet = { ...toBeSet, pagination, collectionFuelData };
            }
            if (fullFuelData !== null) {
              toBeSet = { ...toBeSet, collectionFullFuelData };
            }
            if (levels !== null) {
              toBeSet = { ...toBeSet, collectionFuelLevels };
            }
            this.setState(toBeSet);
            this.onFuelDataAvailable();
          });
        }
      );
    });
  };

  onDownload = () => {
    const { translationsStore, car } = this.props;
    let { startDate, endDate, fuelType } = this.state;
    startDate = moment(startDate).format('YYYY-MM-DD');
    endDate = moment(endDate).format('YYYY-MM-DD');
    let collectionFullFuelData = [];
    this.setState({ loadingFullFuelData: true });
    api('getFuelVariations', car.id, startDate, endDate, fuelType).then(response => {
      ({ rows: collectionFullFuelData = [] } = response);
      this.solveCollectionsGeocoding(collectionFullFuelData)
        .then(([collectionFullFuelData]) => {
          this.setState({ collectionFullFuelData });
          return Promise.resolve(collectionFullFuelData);
        })
        .catch(e => e)
        .then(collectionFullFuelData => {
          this.setState({ loadingFullFuelData: false });
          if (collectionFullFuelData.length > 0) {
            const fields = [
              {
                label: translationsStore.translate('quickFocus.fuel.columns.labelPercentageChange'),
                value: row => row.fuelLevelChange
              },
              {
                label: translationsStore.translate('quickFocus.fuel.columns.labelDate'),
                value: row => moment(row.createdAt).format('DD/MM/YYYY HH:mm')
              },
              {
                label: translationsStore.translate('quickFocus.fuel.columns.labelPosition'),
                value: row => row.positionLabel
              }
            ];

            const filename = 'myfleet.csv';
            download.csv({ fields, rawdata: collectionFullFuelData, filename });
          }
        });
    });
  };

  onPageChange = (page, pageSize) => {
    let { pagination } = this.state;
    pagination = { ...pagination, current: page };
    this.setState({ pagination }, () => {
      const { car } = this.props;
      this.loadFuelData({ car, withFuelData: true });
    });
  };

  onTableChange = (pagination, filters, sorter) => {
    // pagination, filters are skipped here
    let [sort, order] = [undefined, undefined];
    if (Object.keys(sorter).length !== 0 && sorter.constructor === Object) {
      sort = sorter.field;
      order = sorter.order === 'descend' ? 'DESC' : 'ASC';
    }
    this.setState({ sort, order }, () => {
      const { car } = this.props;
      this.loadFuelData({ car, withFuelData: true });
    });
  };

  onChangeStartDate = startDate => {
    this.filterChanged(startDate, this.state.endDate);
    if (moment(startDate).isBefore(moment(this.state.endDate))) {
      return false;
    } else {
      this.filterChanged(startDate, this.state.endDate);
    }
  };

  onChangeEndDate = endDate => {
    if (moment(endDate).isBefore(moment(this.state.startDate))) {
      return false;
    } else {
      this.filterChanged(this.state.startDate, endDate);
    }
  };

  filterChanged = (startDate, endDate) => {
    const { car } = this.props;
    this.setState({ startDate, endDate }, () => {
      this.loadFuelData({ car, withFuelData: true, withFullFuelData: true, withFuelLevels: true });
    });
  };

  onChangeType = fuelType => {
    const { car } = this.props;
    this.setState({ fuelType }, () => {
      this.loadFuelData({ car, withFuelData: true, withFullFuelData: true, withFuelLevels: true });
    });
  };

  renderGlobalReliability = () => {
    const {
      translationsStore,
      car: { fuelLevelReliability }
    } = this.props;
    const style = { textTransform: 'uppercase', fontWeight: 700 };
    let color = undefined;
    if (fuelLevelReliability === 'high') {
      color = 'var(--green)';
    } else if (fuelLevelReliability === 'medium') {
      color = 'var(--orange-dtc-color)';
    } else {
      color = 'var(--red-dtc-color)';
    }
    return (
      <div style={{ display: 'flex', alignItems: 'center', textTransform: 'uppercase', fontWeight: 700, color }}>
        {translationsStore.translate('quickFocus.fuel.globalReliability')}
        <div style={{ ...style, width: 8, height: 8, margin: 10, borderRadius: '50%', backgroundColor: color }} />
      </div>
    );
  };

  renderEventPopChart = eventData => {
    const { translationsStore, commonStore } = this.props;
    const { reliability, previousOccurances, value, nextOccurances } = eventData;
    const title = `${translationsStore.translate(`quickFocus.fuel.types.${eventData.type}`)} ${moment(eventData.createdAt).format(commonStore.longDateFormat.L)} ${moment(
      eventData.createdAt
    ).format(commonStore.longDateFormat.LT)}`;

    const valid = !(reliability === 'unknown' && !isArray(previousOccurances) && !isArray(nextOccurances));

    const chartData = [
      ...(previousOccurances || []).map(occ => ({ value: occ, hot: false })),
      { value, hot: true },
      ...(nextOccurances || []).map(occ => ({ value: occ, hot: false }))
    ].map((occ, index) => ({ ...occ, key: index }));

    const dotRender = ({ ...parameters }) => {
      if (parameters.payload.hot === true) {
        return (
          <svg key={parameters.key} x={parameters.cx - 10} y={parameters.cy - 10} height="20" width="20">
            <circle cx="10" cy="10" r="10" fill={this.chartsStyle.stroke} />
          </svg>
        );
      }
      return (
        <svg key={parameters.key} x={parameters.cx - 3} y={parameters.cy - 3} height="6" width="6">
          <circle cx="3" cy="3" r="3" fill={this.chartsStyle.stroke} />
        </svg>
      );
    };

    const popContent = (
      <Fragment>
        <div style={{ fontWeight: 700, textAlign: 'center', marginBottom: 25 }}>{title}</div>
        <LineChart
          width={450}
          height={200}
          data={chartData}
          margin={{
            top: 0,
            right: 0,
            left: -20,
            bottom: 0
          }}
        >
          <CartesianGrid vertical={false} />
          <XAxis dataKey="key" tick={{ fill: 'var(--dark-grey)', fontSize: 14 }} tickFormatter={item => `·`} axisLine={false} tickLine={false} padding={{ left: 50, right: 50 }} />
          <YAxis tick={{ fill: 'var(--grey)', fontSize: 12 }} tickFormatter={item => `${item}%`} axisLine={false} tickLine={false} domain={[0, 100]} />
          <Line isAnimationActive={false} dataKey="value" stroke={this.chartsStyle.stroke} strokeWidth={2} dot={dotRender} />
        </LineChart>
      </Fragment>
    );
    if (valid) {
      return (
        <Popover overlayClassName="FuelPop" content={popContent} placement={'right'}>
          <GraphIcon width="19px" height="18px" style={{ color: 'var(--dark-grey)', margin: '0px 25px' }} />
        </Popover>
      );
    } else {
      return <GraphIcon width="19px" height="18px" style={{ color: 'var(--light-grey)', margin: '0px 25px' }} />;
    }
  };

  dotLevelsRender = ({ ...parameters }) => {
    const { payload } = parameters;
    const { translationsStore, commonStore } = this.props;
    const { collectionFuelData: rows } = this.state;
    const matchedRow = rows.find(row => new Date(row.createdAt).valueOf() === payload.date);
    if (matchedRow !== undefined && matchedRow.reliability !== 'unknown') {
      let changeStyle = { fontWeight: 'bold', color: 'var(--green)' };
      if (matchedRow.fuelLevelChange < 0) {
        changeStyle = { ...changeStyle, color: 'var(--red-dtc-color)' };
      }
      const content = (
        <div className="FuelEventPop">
          <div>
            <div>{translationsStore.translate(`quickFocus.fuel.types.${matchedRow.type}`)}</div>
            <div>
              {moment(matchedRow.createdAt).format(commonStore.longDateFormat.L)}
              <br />
              {moment(matchedRow.createdAt).format(commonStore.longDateFormat.LT)}
            </div>
          </div>
          <div>
            {translationsStore.translate('quickFocus.fuel.eventPosition')}: {matchedRow.positionLabel}
          </div>
          <div>
            {translationsStore.translate('quickFocus.fuel.eventChange')}: <span style={changeStyle}>{matchedRow.fuelLevelChange}%</span>
          </div>
        </div>
      );
      let fill = 'var(--green)';
      if (matchedRow.fuelLevelChange < 0) {
        fill = 'var(--red-dtc-color)';
      }
      return (
        <Fragment key={parameters.key}>
          <Popover overlayClassName="FuelEventPopWrapper" content={content}>
            <svg x={parameters.cx - 20} y={parameters.cy - 20} height="40" width="40">
              <circle cx="20" cy="20" r="5" fill={fill} stroke="#ffffff" strokeWidth={2} />
            </svg>
          </Popover>
        </Fragment>
      );
    } else {
      return false;
    }
  };

  render() {
    const { translationsStore, commonStore, visible, onOk, onCancel } = this.props;
    const { loadingFullFuelData, startDate, endDate, fuelType, pagination } = this.state;

    let { collectionFuelData, collectionFuelLevels } = this.state;
    collectionFuelData = collectionFuelData.map((item, index) => ({ ...item, key: index }));

    const columns = [
      {
        key: 'fuelLevelChange',
        dataIndex: 'fuelLevelChange',
        title: translationsStore.translate('quickFocus.fuel.columns.labelPercentageChange'),
        className: 'CenterNoWrap',
        render: (value, record) => {
          let style = { fontWeight: 'bold', color: 'var(--green)' };
          if (record.fuelLevelChange < 0) {
            style = { ...style, color: 'var(--red-dtc-color)' };
          }
          return (
            <div className="fuelDeepData">
              <div className={`fuelDeepDataReliability ${record.reliability}`} />
              {this.renderEventPopChart(record)}
              <div style={style}>{value}%</div>
            </div>
          );
        },
        sorter: true
      },
      {
        key: 'createdAt',
        dataIndex: 'createdAt',
        title: translationsStore.translate('quickFocus.fuel.columns.labelDate'),
        className: 'CenterNoWrap',
        render: timestamp => `${moment(timestamp).format(commonStore.longDateFormat.L)} ${moment(timestamp).format(commonStore.longDateFormat.LT)}`,
        sorter: true
      },
      {
        key: 'position',
        dataIndex: 'position',
        title: translationsStore.translate('quickFocus.fuel.columns.labelPosition'),
        className: 'Center',
        width: 380,
        render: (text, record) => `${record.positionLabel}`,
        sorter: false
      }
    ];

    let paginationText = '';
    const start = pagination.pageSize * (pagination.current - 1) + 1;
    const end = Math.min(pagination.total, start + pagination.pageSize - 1);
    paginationText = `${start}-${end} di ${pagination.total}`;

    const chartData = (collectionFuelLevels || []).map(sample => ({ date: new Date(sample.createdAt).valueOf(), value: sample.value }));

    const fuelTypes = [
      {
        key: 'refueling',
        title: translationsStore.translate('quickFocus.fuel.types.refueling')
      },
      {
        key: 'theft',
        title: translationsStore.translate('quickFocus.fuel.types.theft')
      }
    ];

    const startReference = moment(startDate)
      .startOf('day')
      .toDate();
    const endReference = moment(endDate)
      .endOf('day')
      .toDate();

    return (
      <AirTabbedModal
        className="FuelDataModal"
        tabs={[
          {
            key: 'list',
            title: translationsStore.translate('quickFocus.fuel.views.list'),
            render: (
              <Spin spinning={this.state.loadingFuelData}>
                <div className="FuelDataWrapper FuelDataWrapperList">
                  <div>
                    {this.renderGlobalReliability()}
                    <AirButton
                      bgcolor="transparent"
                      color={loadingFullFuelData ? 'var(--disabled-color)' : 'var(--selected-color)'}
                      size="large"
                      icon={<DownloadIcon style={{ width: '13px', height: '16px', color: 'var(--selected-color)', marginRight: '11px' }} />}
                      label={translationsStore.translate('quickFocus.fuel.filter.download')}
                      onClick={this.onDownload}
                      disabled={loadingFullFuelData}
                    />
                  </div>
                  <div className="daterange">
                    <AirDatePicker
                      onChange={this.onChangeStartDate}
                      selectsStart
                      startDate={startDate}
                      endDate={endDate}
                      duration
                      prefix={translationsStore.translate('quickFocus.fuel.filter.date.from')}
                      selected={startDate}
                      dateFormat="dd MMM YYY"
                    />
                    <AirDatePicker
                      onChange={this.onChangeEndDate}
                      selectsEnd
                      startDate={startDate}
                      endDate={endDate}
                      prefix={translationsStore.translate('quickFocus.fuel.filter.date.to')}
                      selected={endDate}
                      dateFormat="dd MMM YYY"
                    />
                    <Select
                      className="FuelTypeSelect"
                      value={fuelType}
                      allowClear={true}
                      onChange={this.onChangeType}
                      placeholder={translationsStore.translate('quickFocus.fuel.filter.labelType')}
                      suffixIcon={<ArrowDownIcon />}
                    >
                      {fuelTypes.map((type, index) => (
                        <Option key={index} value={type.key}>
                          {type.title}
                        </Option>
                      ))}
                    </Select>
                  </div>
                  <Table
                    className="FuelDataTable"
                    columns={columns}
                    dataSource={collectionFuelData}
                    pagination={false}
                    onChange={this.onTableChange}
                    rowClassName={record => record.withAlert && 'withAlert'}
                  />
                  {pagination !== false && pagination.total > pagination.pageSize && (
                    <div className="FuelDataPagination">
                      <div>{paginationText}</div>
                      <Pagination {...pagination} />
                    </div>
                  )}
                </div>
              </Spin>
            )
          },
          {
            key: 'graph',
            title: translationsStore.translate('quickFocus.fuel.views.graph'),
            render: (
              <Spin spinning={this.state.loadingFuelData}>
                <div className="FuelDataWrapper FuelDataWrapperGraph">
                  <div>
                    <div className="daterange">
                      <AirDatePicker
                        onChange={this.onChangeStartDate}
                        selectsStart
                        startDate={startDate}
                        endDate={endDate}
                        duration
                        prefix={translationsStore.translate('quickFocus.fuel.filter.date.from')}
                        selected={startDate}
                      />
                      <AirDatePicker
                        onChange={this.onChangeEndDate}
                        selectsEnd
                        startDate={startDate}
                        endDate={endDate}
                        prefix={translationsStore.translate('quickFocus.fuel.filter.date.to')}
                        selected={endDate}
                      />
                    </div>
                    <AirButton
                      bgcolor="transparent"
                      color={loadingFullFuelData ? 'var(--disabled-color)' : 'var(--selected-color)'}
                      size="large"
                      icon={<DownloadIcon style={{ width: '13px', height: '16px', color: 'var(--selected-color)', marginRight: '11px' }} />}
                      label={translationsStore.translate('quickFocus.fuel.filter.download')}
                      onClick={this.onDownload}
                      disabled={loadingFullFuelData}
                    />
                  </div>

                  <div style={{ boxShadow: '0 0 3px 2px rgba(0, 0, 0, 0.03)', paddingBottom: 20 }}>
                    <div
                      style={{
                        color: 'var(--dark-grey)',
                        textTransform: 'uppercase',
                        fontWeight: 700,
                        fontSize: 16,
                        height: 20,
                        marginTop: 30,
                        marginLeft: 24,
                        marginBottom: 60
                      }}
                    >
                      {translationsStore.translate('quickFocus.fuel.graph.chartTitle')}
                    </div>
                    {chartData.length > 0 && (
                      <AreaChart
                        width={750}
                        height={400}
                        data={chartData}
                        margin={{
                          top: 10,
                          right: 30,
                          left: 0,
                          bottom: 30
                        }}
                      >
                        <CartesianGrid vertical={false} />
                        <XAxis
                          // tick={{ fill: 'var(--grey)', fontSize: 12 }}
                          // tickFormatter={item => moment(item).format(commonStore.longDateFormat.L)}
                          tick={<ChartTick fontSize={12} fill={'var(--grey)'} />}
                          dataKey="date"
                          axisLine={false}
                          tickLine={false}
                          tickMargin={20}
                          minTickGap={50}
                          domain={[startDate.valueOf(), endDate.valueOf()]}
                          type="number"
                          scale="utc"
                        />
                        <ReferenceLine
                          x={moment(startDate).valueOf()}
                          stroke="transparent"
                          ifOverflow="extendDomain"
                          label={
                            <ReferenceLabel fontSize={12} fill={'var(--grey)'} textAnchor="start" offsetX={0} offsetY={26}>
                              {moment(startReference).format(commonStore.longDateFormat.L)}
                            </ReferenceLabel>
                          }
                        />
                        <ReferenceLine
                          x={moment(endReference).valueOf()}
                          stroke="transparent"
                          ifOverflow="extendDomain"
                          label={
                            <ReferenceLabel fontSize={12} fill={'var(--grey)'} textAnchor="end" offsetX={0} offsetY={26}>
                              {moment(endReference).format(commonStore.longDateFormat.L)}
                            </ReferenceLabel>
                          }
                        />
                        <YAxis tick={{ fill: 'var(--grey)', fontSize: 12 }} tickFormatter={item => `${item}%`} axisLine={false} tickLine={false} domain={[0, 100]} />
                        <Area dataKey="value" stroke={this.chartsStyle.stroke} strokeWidth={2} fill={this.chartsStyle.fill} dot={this.dotLevelsRender} />
                      </AreaChart>
                    )}
                  </div>
                </div>
              </Spin>
            )
          }
        ]}
        visible={visible}
        width={896}
        onOk={onOk}
        onCancel={onCancel}
      />
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/App/ClientListWithMap/ClientDetail/QuickFocus/FuelDataModal/FuelDataModal.js